import React from 'react';
import { DOMAINS } from '../../../shared/constants/domains';

/**
 * Exam Simulator home screen: start Full/Quick/Weak Spots exam.
 */
export default function ExamHome({ questionCount, pastExams, onStartExam }) {
  return (
    <div className="px-4 py-6 max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Exam Pressure Simulator</h1>
        <p className="text-gray-500 text-sm">Build exam-taking stamina under timed conditions</p>
      </div>

      {/* Exam modes */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {/* Full Practice Exam */}
        <button
          onClick={() => onStartExam({ type: 'full', questionCount: 102, timeLimitMinutes: 120 })}
          className="bg-white border-2 border-gray-200 rounded-xl p-6 text-left hover:border-blue-500 hover:shadow-sm transition-all"
        >
          <div className="text-3xl mb-3">📋</div>
          <h3 className="font-bold text-gray-900 mb-1">Full Practice Exam</h3>
          <p className="text-sm text-gray-500 mb-3">102 questions, 120 minutes. Mirrors the real ENCOR.</p>
          <div className="text-xs text-gray-400">Domain-weighted distribution</div>
        </button>

        {/* Quick Quiz */}
        <button
          onClick={() => onStartExam({ type: 'quick', questionCount: 20, timeLimitMinutes: 30 })}
          className="bg-white border-2 border-gray-200 rounded-xl p-6 text-left hover:border-green-500 hover:shadow-sm transition-all"
        >
          <div className="text-3xl mb-3">⚡</div>
          <h3 className="font-bold text-gray-900 mb-1">Quick Quiz</h3>
          <p className="text-sm text-gray-500 mb-3">20 questions, 30 minutes. Fast domain-focused practice.</p>
          <div className="flex flex-wrap gap-1">
            {DOMAINS.map(d => (
              <button
                key={d.id}
                onClick={(e) => {
                  e.stopPropagation();
                  onStartExam({ type: 'quick', domain: d.id, questionCount: 20, timeLimitMinutes: 30 });
                }}
                className="text-xs px-2 py-0.5 rounded"
                style={{ backgroundColor: `${d.color}20`, color: d.color }}
              >
                {d.shortName}
              </button>
            ))}
          </div>
        </button>

        {/* Weak Spots */}
        <button
          onClick={() => onStartExam({ type: 'weak_spots', questionCount: 30, timeLimitMinutes: 40 })}
          disabled={pastExams.length === 0}
          className="bg-white border-2 border-gray-200 rounded-xl p-6 text-left hover:border-red-500 hover:shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="text-3xl mb-3">🎯</div>
          <h3 className="font-bold text-gray-900 mb-1">Weak Spots</h3>
          <p className="text-sm text-gray-500 mb-3">
            {pastExams.length > 0
              ? '30 questions from your historically weakest areas.'
              : 'Complete at least one exam first.'
            }
          </p>
          <div className="text-xs text-gray-400">Auto-generated from past performance</div>
        </button>
      </div>

      {/* Stats */}
      <div className="text-center text-sm text-gray-500">
        <p>{questionCount} questions in the pool • {pastExams.length} exams completed</p>
      </div>

      {/* Past exams */}
      {pastExams.length > 0 && (
        <div className="mt-8">
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">Past Exams</h3>
          <div className="space-y-2">
            {pastExams.slice(-5).reverse().map(exam => (
              <div key={exam.id} className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-gray-900">
                    {exam.type === 'full' ? 'Full Exam' : exam.type === 'quick' ? 'Quick Quiz' : 'Weak Spots'}
                  </span>
                  <span className="text-xs text-gray-400 ml-2">
                    {new Date(exam.started_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`font-bold ${exam.score.passed ? 'text-green-600' : 'text-red-600'}`}>
                    {exam.score.percentage}%
                  </span>
                  <a
                    href={`/exam/review/${exam.id}`}
                    className="text-xs text-blue-600 hover:text-blue-700"
                  >
                    Review
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
